import { clamp, times } from 'lodash'
import React from 'react'

import { negation, toRadians } from '@/utils'

import {
  RADIAL_LOADER_MAX_BARS_AMOUNT,
  RADIAL_LOADER_MIN_ANIMATION_DURATION,
  RADIAL_LOADER_MIN_BARS_AMOUNT,
} from './radial-loader.constants'
import * as S from './radial-loader.styled'
import { RadialLoaderProps } from './radial-loader.types'

export const RadialLoader = ({
  className = '',
  wrapperClassName = '',
  barClassName = '',
  color = 'currentColor',
  barWidth = 0.5,
  barHeight = 0.15,
  barsAmount = 8,
  distanceFromCenter = 0.5,
  animationDuration = 1000,
  offset = 0,
}: RadialLoaderProps) => {
  const barsAmountClamped = clamp(
    barsAmount,
    RADIAL_LOADER_MIN_BARS_AMOUNT,
    RADIAL_LOADER_MAX_BARS_AMOUNT,
  )
  const animationDurationClamped = Math.max(animationDuration, RADIAL_LOADER_MIN_ANIMATION_DURATION)

  const normalizedAngle = 360 / barsAmountClamped
  const normalizedDuration = animationDurationClamped / barsAmountClamped
  const halfWidth = barWidth / 2
  const totalSize = barWidth * 2 + distanceFromCenter * 2

  let oddDegrees = barsAmountClamped % 2 === 0 ? 0 : normalizedAngle / 4
  oddDegrees = negation(oddDegrees, barsAmountClamped % 4 <= 2)

  const containerInlineStyles: React.CSSProperties = {
    height: totalSize + 'rem',
    width: totalSize + 'rem',
  }

  const wrapperInlineStyles: React.CSSProperties = {
    transform: `rotate(${normalizedAngle * ((barsAmountClamped + offset) % barsAmountClamped) + oddDegrees}deg)`,
  }

  const barGlobalInlineStyles: React.CSSProperties = {
    animationDuration: animationDurationClamped + 'ms',
    backgroundColor: color,
    borderColor: color,
    // NOTE: We play out that 'height' is built by 'borderWidth', so use rems for it as well
    borderWidth: barHeight + 'rem',
    width: barWidth + 'rem',
  }

  return (
    <S.Container className={className} style={containerInlineStyles}>
      <S.Wrapper className={wrapperClassName} style={wrapperInlineStyles}>
        {times(barsAmountClamped, (i) => {
          const delay = normalizedDuration * i
          const angle = normalizedAngle * i
          const sin = Math.sin(toRadians(angle)) // ordinate - y
          const cos = Math.cos(toRadians(angle)) // abscissa - x

          const y = (halfWidth + distanceFromCenter) * sin
          const x = (halfWidth + distanceFromCenter) * cos

          const barInlineStyles: React.CSSProperties = {
            ...barGlobalInlineStyles,
            animationDelay: delay + 'ms',
            transform: `translate(calc(-50% + ${x}rem), calc(-50% + ${y}rem)) rotate(${angle}deg)`,
          }

          return <S.Bar key={i} className={barClassName} style={barInlineStyles} />
        })}
      </S.Wrapper>
    </S.Container>
  )
}
